/**
 * Quién entra a la consola.
 *
 * Aclaración importante: esta comprobación es de INTERFAZ, no de seguridad. La
 * seguridad la hace el backend en cada endpoint —y la haría igual aunque esta
 * pantalla no existiera—. Lo que resuelve acá es no mostrarle a una persona un
 * tablero entero de botones que después le van a devolver 403.
 */

import type { SessionUser } from '@/lib/types';

/** Grupo que administra la organización (ver `nucleo/modules/base/security.py`). */
export const GRUPO_ADMINISTRADOR = 'base.administrador';

export function esAdministrador(usuario: SessionUser | null): boolean {
  if (usuario === null) return false;
  if (usuario.is_superuser) return true;
  return usuario.groups.includes(GRUPO_ADMINISTRADOR);
}

/** Nombre para mostrar: el real si lo hay, si no el usuario. */
export function nombreVisible(usuario: SessionUser | null): string {
  if (usuario === null) return '—';
  const nombre = usuario.name.trim();
  return nombre === '' ? usuario.username : nombre;
}

// ---------------------------------------------------------------------------
// Estado de la cuenta de una persona
// ---------------------------------------------------------------------------
/**
 * Los tres estados posibles de una cuenta, en un solo lugar.
 *
 * Existe porque la misma persona figuraba **«Sin activar» en la lista** y
 * **«Activo» (chip verde) en su ficha**, en la misma pantalla y al mismo tiempo.
 * No eran dos errores: eran dos criterios distintos con nombres parecidos. La
 * lista miraba `puede_entrar` (¿ya eligió su contraseña?) y la ficha miraba
 * `is_active` (¿la cuenta está dada de alta?). Las dos preguntas son legítimas y
 * la respuesta puede diferir; lo que no puede es que las dos se llamen igual.
 *
 * El vocabulario queda fijado así, y lo usan la lista y la ficha:
 *
 * - **Sin activar** — la cuenta existe, pero la persona todavía no canjeó su
 *   enlace, así que no tiene contraseña y no puede entrar.
 * - **Desactivada** — se le dio de baja. No entra, y no entra aunque tenga
 *   contraseña.
 * - **Activa** — puede entrar hoy. Es la conjunción de las dos cosas.
 *
 * `puedeEntrar` llega de `GET /acceso/estado/`; mientras ese padrón no cargó es
 * `undefined` y no se afirma nada (`null`), en vez de dar por activa a una
 * persona sobre la que todavía no se sabe.
 */
export type EstadoDeCuenta = {
  clave: 'activa' | 'sin_activar' | 'desactivada';
  etiqueta: string;
  tono: 'success' | 'accent' | 'warning';
  detalle: string;
};

export function estadoDeCuenta(
  activo: boolean,
  puedeEntrar: boolean | undefined,
): EstadoDeCuenta | null {
  if (!activo) {
    return {
      clave: 'desactivada',
      etiqueta: 'Desactivada',
      tono: 'warning',
      detalle: 'La cuenta está dada de baja: no puede entrar.',
    };
  }
  if (puedeEntrar === undefined) return null;
  if (!puedeEntrar) {
    return {
      clave: 'sin_activar',
      etiqueta: 'Sin activar',
      tono: 'accent',
      detalle: 'Todavía no eligió su contraseña, así que no puede entrar.',
    };
  }
  return {
    clave: 'activa',
    etiqueta: 'Activa',
    tono: 'success',
    detalle: 'Eligió su contraseña y puede entrar.',
  };
}
